<script>
/**
 * Imports
 */
import SignupFormGroup from "./../form/SignupFormGroup.vue";

/**
 * Component: Sections: Users
 *
 * The component used for displaying the Users section
 * on signup form. The fields and the data is configured
 * from wp-admin and Sections CPT.
 *
 * @author PH
 */
export default {
  data() {
    return {
      l18n: {},
      settings: {}
    };
  },

  created: function() {
    this.l18n = this.getData("l18n");
    this.settings = this.getData("settings");

    // Initialize the first fields arter component load.
    this.addUser({
      title: "Enter user(s) information. Add as many as needed."
    });
  },

  computed: {
    valid: {
      get() {
        return this.$store.getters.getSectionData("users", "valid");
      },
      set(value) {
        this.$store.commit("updateSection", {
          section: "users",
          values: {
            valid: value
          }
        });
      }
    },

    active() {
      return this.$store.getters.getSectionData("users", "active");
    },

    next() {
      return this.$store.getters.getSectionData("users", "next");
    },

    submitLabel() {
      if (this.settings.submitLabel) {
        return this.settings.submitLabel;
      }
      return this.l18n.submit.continue;
    },

    addUserLabel() {
      return this.l18n.users.add;
    }
  },

  methods: {
    /**
     * Submit: Form
     *
     * @author PH
     * @return void
     */
    submitForm: function(e) {
      e.preventDefault();

      let valid = true;
      let section = this.$store.getters.getSection("users");

      if (section && section.fields) {
        section.fields.forEach(field => {
          if (!field.valid) {
            this.$store.commit("updateInput", {
              id: field.id,
              section: "users",
              values: {
                valid: false
              }
            });

            valid = false;
          }
        });
      }

      this.valid = valid;

      if (this.valid) {
        this.$store.commit("changeSection", {
          id: this.next
        });
      }
    },

    /**
     * Get: Data
     *
     * @author PH
     * @return string|bool
     */
    getData: function(property) {
      if (property) {
        return this.$store.getters.getSectionData("users", property);
      }
      return false;
    },

    /**
     * Get: Group
     *
     * Function filters all the fields and gets
     * fields which are needed for the specific group.
     *
     * @author PH
     * @return array
     */
    getFields(group) {
      return this.getData("fields").filter(field => {
        return field.group === group;
      });
    },

    /**
     * Add: User
     *
     * Function generates the new user group based on default fields.
     * It calculates the next index based on group size and uses
     * that for generating the new fields in the users section.
     *
     * Each user belongs to unique group (e.g. user-1), the fields have
     * unique ids and names (e.g. first_name-1).
     *
     * The default fields are used for generating the new inputs.
     *
     * @author PH
     * @return void
     */
    addUser(props) {
      let index = "";
      let fields = [];

      const sectionUsers = this.$store.getters.getSection("users");
      const sectionUsersIndex = this.$store.getters.getSectionIndex("users");

      if (sectionUsers) {
        // The nex index is calculated based on last element id. It's always next number.
        if (0 === sectionUsers.groups.length) {
          index = 1;
        } else {
          index =
            parseInt(
              sectionUsers.groups[sectionUsers.groups.length - 1].id.replace(
                "user-",
                ""
              )
            ) + 1;
        }

        if (
          sectionUsers.defaultFields &&
          0 < sectionUsers.defaultFields.length
        ) {
          sectionUsers.defaultFields.forEach(defaultField => {
            let field = {};

            for (let prop in defaultField) {
              if (defaultField.hasOwnProperty(prop)) {
                field[prop] = defaultField[prop];
              }
            }

            field.group += "-" + index;
            field.id += "-" + index;
            field.name += "-" + index;

            fields.push(field);
          });
        }
      }

      if ("" !== index && 0 < fields.length) {
        this.$store.commit("updateSectionGroups", {
          section: "users",
          group: "",
          values: {
            id: "user-" + index,
            title: 1 === index && props && "" !== props.title ? props.title : ""
          }
        });

        this.$store.commit("updateSectionFields", {
          section: "users",
          fields: fields
        });
      }
    },

    /**
     * Remove: User
     *
     * Function triggers user group removing. The removing
     * is based on group id, e.g. `user-1`.
     *
     * @author PH
     * @return void
     */
    removeUser(userId) {
      this.$store.commit("updateSectionGroups", {
        section: "users",
        group: userId,
        remove: true
      });

      this.$store.commit("updateSectionFields", {
        section: "users",
        group: userId,
        remove: true
      });
    }
  },

  components: { SignupFormGroup }
};
</script>

<template>
  <div
    class="signup-section signup-section-users"
    :class="{ valid: valid === true, invalid: valid === false }"
    v-if="active"
  >
    <form action method="POST" @submit="submitForm">
      <div class="signup-section__header">
        <h2>{{getData('header')}}</h2>
        <p v-if="getData('introduction')">{{ getData('introduction') }}</p>
      </div>

      <div class="signup-section__content">
        <signup-form-group
          v-for="group in getData('groups')"
          v-bind:key="group.id"
          v-bind:title="group.title"
          v-bind:fields="getFields(group.id)"
          section="users"
        >
          <template v-slot:beforeInputs v-if="'user-1' !== group.id">
            <button type="button" class="signup-form-group__remove" @click="removeUser(group.id)"></button>
          </template>
        </signup-form-group>
      </div>

      <div class="signup-section__cta">
        <button type="button" @click="addUser()">+ {{addUserLabel}}</button>
      </div>

      <div class="signup-section__footer">
        <div class="signup-section__notifications" v-if="valid === false">
          <p>{{l18n.submit.error}}</p>
        </div>
        <div class="signup-section__submit">
          <button type="submit">{{submitLabel}}</button>
        </div>
      </div>
    </form>
  </div>
</template>

